import { SegmentEffects } from '@opengov/app-core/analytics';
import {
  COPY_WITHOUT_STATUS,
  COPY_WITH_STATUS,
  MAX_DESCRIPTION_LENGTH,
  MAX_TITLE_LENGTH,
  MIN_TITLE_LENGTH,
  PUBLICATIONS_MANAGER_PRIVILEGE,
  UNIQUENESS_VIOLATION
} from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import React, { useEffect, useMemo, useState } from 'react';

import {
  publicationList as publicationListLocale,
  publicationView as publicationViewLocale
} from '../../../../locale/stories.en.locale.json';
import { IExtendedStoryPrivilege, IPublicationModalMessages, ISessionUser } from '../../../../types';
import { useCreatePublication } from '../PublicationList/hooks/usePublicationMutations';
import { useClonePublication } from '../PublicationView/hooks/usePublicationMutations';

import { navigateToPublicationViewNewTab } from '../../../state';

import { styles } from './CopyPublicationModal.styles';

export type ISelectOption = {
  id: string;
  displayName: string;
};

export interface ICopyPublicationModalProps {
  isAdmin?: boolean;
  isBulkUpdateStoryStatusEnabled: boolean;
  messages: IPublicationModalMessages;
  user?: ISessionUser;
  sourcePublication: IPublication;
  entityName?: string;
  entityId: number;
  entityUuid: string;
  entitySubdomain: string;
  allPublicationsTitles: string[];
  currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null;
  onClose(): void;
}

export const CopyPublicationModal: React.FC<ICopyPublicationModalProps> = (props) => {
  const {
    isAdmin,
    isBulkUpdateStoryStatusEnabled,
    entityId,
    entityUuid,
    entitySubdomain,
    messages,
    onClose,
    allPublicationsTitles,
    sourcePublication,
    currentSubjectPublicationStoryClaim
  } = props;
  const [hasSameName, setHasSameName] = useState(false);
  const [isloading, setIsloading] = useState(false);
  const destinationPublicationTitle = `${messages.copyOf} ${sourcePublication.title}`;
  const [publicationTitle, setPublicationTitle] = useState(destinationPublicationTitle);
  const [copyType, setCopyType] = useState(COPY_WITH_STATUS);
  const trimmedPublicationTitle = useMemo(() => publicationTitle.trim(), [publicationTitle]);
  const [publicationDescription, setPublicationDescription] = useState(sourcePublication.description || '');
  const trimmedPublicationDescription = useMemo(() => publicationDescription.trim(), [publicationDescription]);

  const createMutation = useCreatePublication({
    title: publicationListLocale.somethingWentWrongError,
    body: publicationListLocale.failedToCreatePublication
  });
  const clonePublicationMutation = useClonePublication({
    title: publicationViewLocale.publicationCopyFailed,
    body: publicationViewLocale.publicationCopyFailedDescription
  });

  useEffect(() => {
    if (allPublicationsTitles) {
      const hasMatch = !!allPublicationsTitles.find(
        (publication) => publication.toLowerCase() === trimmedPublicationTitle.toLowerCase()
      );
      setHasSameName(hasMatch);
    }
  }, [trimmedPublicationTitle, allPublicationsTitles]);

  const handleConfirmCreatePublication = () => {
    if (!isAdmin) return;
    setIsloading(true);
    createMutation.mutate(
      {
        title: trimmedPublicationTitle,
        description: trimmedPublicationDescription,
        entityId,
        entitySubdomain,
        folderId: '',
        createSubjectClaim: !currentSubjectPublicationStoryClaim?.includes(PUBLICATIONS_MANAGER_PRIVILEGE)
      },
      {
        onSuccess: async (publication: IPublication) => {
          // Step 2: clone content from source → destination via the Publication
          // View clone hook (replaces the retired compose-app clone effect).
          const copyPublicationJobId = await clonePublicationMutation.mutateAsync({
            sourcePublicationId: sourcePublication.id,
            destinationPublicationId: publication.id,
            copyType,
            entityId,
            entityUuid,
            entitySubdomain
          });
          void SegmentEffects.track({
            eventName: 'Stories: Publications Copied',
            props: {}
          });
          navigateToPublicationViewNewTab(entitySubdomain, publication.id, copyPublicationJobId);
          onClose();
        },
        onError: (error: Error) => {
          // Only a duplicate-title response surfaces here as inline validation.
          // Every other failure is already reported by the hook's error toast,
          // so flagging the name would be misleading.
          if (error?.message?.includes(UNIQUENESS_VIOLATION)) {
            setHasSameName(true);
          }
        },
        onSettled: () => {
          setIsloading(false);
        }
      }
    );
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="copy-publication-dialog-title">
      <DialogTitle id="copy-publication-dialog-title" sx={styles.dialogTitle}>
        {messages.copyPublication}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box data-test="copy-publication-form">
          <Box>
            <Box sx={styles.labelRow}>
              <Typography id="copy-publication-name-label" variant="body2" sx={styles.formLabel}>
                {messages.publicationName}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {publicationTitle.length}/{MAX_TITLE_LENGTH}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={messages.enterNamePlaceholder}
              value={publicationTitle}
              onChange={(e) => setPublicationTitle(e.target.value)}
              inputProps={{ 'data-test': 'publication-title-input', maxLength: MAX_TITLE_LENGTH, className: 'no-format', 'aria-labelledby': 'copy-publication-name-label' }}
            />
            {hasSameName && (
              <FormHelperText error>
                {messages.noSameName}
              </FormHelperText>
            )}
          </Box>

          <Box sx={styles.publicationDescription}>
            <Box sx={styles.labelRow}>
              <Typography id="copy-publication-description-label" variant="body2" sx={styles.formLabel}>
                {messages.description}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {publicationDescription.length}/{MAX_DESCRIPTION_LENGTH}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              sx={styles.descriptionTextField}
              placeholder={messages.descriptionPlaceholer}
              value={publicationDescription}
              onChange={(e) => setPublicationDescription(e.target.value)}
              inputProps={{ 'data-test': 'publication-description-input', maxLength: MAX_DESCRIPTION_LENGTH, className: 'no-format', 'aria-labelledby': 'copy-publication-description-label' }}
            />
          </Box>

          {isBulkUpdateStoryStatusEnabled && (
            <Box sx={styles.copyOptionSelection}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={styles.formLabel}>{messages.copyOption}</FormLabel>
                <RadioGroup
                  sx={styles.radioGroup}
                  value={copyType}
                  onChange={(_e, value) => setCopyType(value)}
                  data-test="copy-radio-group"
                >
                  <FormControlLabel
                    value={COPY_WITH_STATUS}
                    sx={styles.radioButton}
                    control={
                      <Radio
                        inputProps={{ 'data-test': 'copy-option-with-status', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                      />
                    }
                    label={messages.copyWithStatus}
                  />
                  <FormControlLabel
                    value={COPY_WITHOUT_STATUS}
                    sx={styles.radioButton}
                    control={
                      <Radio
                        inputProps={{ 'data-test': 'copy-option-without-status', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                      />
                    }
                    label={messages.copyWithoutStatus}
                  />
                </RadioGroup>
              </FormControl>
              {copyType === COPY_WITHOUT_STATUS && (
                <Box sx={styles.warningContainer} data-test="copy-without-status-message">
                  <WarningAmberIcon aria-hidden={true} sx={styles.warningIcon} />
                  <Typography variant="body2" color="text.secondary">
                    {messages.copyWithoutStatusWarning}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="cancel-copy-publication-button">
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={
            trimmedPublicationTitle.length < MIN_TITLE_LENGTH ||
            trimmedPublicationTitle.length > MAX_TITLE_LENGTH ||
            trimmedPublicationTitle.length > MAX_DESCRIPTION_LENGTH ||
            hasSameName
          }
          onClick={handleConfirmCreatePublication}
          data-test="copy-publication-button"
          startIcon={isloading ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
        >
          {messages.copy}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
